import { Dialog, colorValue } from '@hai/ui-react';
import { ButtonStateType } from '@hai/ui-react/dist/types';
import { isNil } from 'ramda';
import React, { createContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Status } from 'src/js/component/actionBar/actionBar';
import { NoContentAvailable } from 'src/js/component/no-content-available';
import { SortType } from 'src/js/component/sort-manager';
import { triggerEvent, watchEvent } from 'src/js/events';
import { useAdaptiveFetch } from 'src/js/hook/use-adaptive-fetch';
import { useModalControls } from 'src/js/hook/use-modal-controls';
import { useStores } from 'src/js/hook/use-stores';
import { NotificationVariant } from 'src/js/notification/notification';
import { createAndDispatchNotification } from 'src/js/notification/notification-helper';
import { StreamingActionBar, scrollPanelIntoView } from 'src/js/component/actionBar/streaming-action-bar';
import { fetchVideoInputs } from 'src/js/pages/streaming/video-input/video-input-store';
import { useWatchObject } from 'src/js/store/use-watch';
import { constant } from 'src/js/util/constant';
import { isNilOrEmpty } from 'src/js/util/global-util';
import { AddEssence } from './add-essence-modal';
import { EssenceList } from './essence-list';
import {
  EssenceViewModel,
  essenceIsStarted,
  essenceIsStopped,
  essenceModelToStatus,
  newEssenceViewModel,
} from './essence-models';
import { EssenceAddFormRef } from './essence-settings';
import essenceModels, {
  deleteEssence,
  fetchEssenceResolutions,
  fetchEssences,
  startStopEssence,
} from './essence-store';
import { apiClientWithoutHandler } from 'src/js/api';
import { APIPath } from 'src/js/api/route-path-index';
import { ServicesResponse } from 'src/js/model/api/response/services-response';
import { useLocalStorage } from 'usehooks-ts';

export const EssencesCtx = createContext(undefined);
export const NmosServiceActiveContext = React.createContext(false);

let connectionLost = false;
export const EssencesPage: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { sessionStore } = useStores();

  const [addModalShown, showAddModal, hideAddModal] = useModalControls();
  const addEssenceRef = useRef<EssenceAddFormRef>();
  const [addButtonState, setAddButtonState] = useState<ButtonStateType>(undefined);
  const newModel = newEssenceViewModel();
  const [essences, setEssences] = useState<EssenceViewModel[]>(essenceModels);
  const [initialized, setInitialized] = useState(false);

  const [nmosServiceActive, setNmosServiceActive] = useState(false);
  const fetchServices = () =>
    apiClientWithoutHandler.genericController.get<any>(APIPath.services.status).then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          const services = response as ServicesResponse;
          if (services.nmos && services.status?.nmos) {
            setNmosServiceActive(true);
          } else {
            setNmosServiceActive(false);
          }
        }
      }
      return res;
    });
  const interval = constant.defaultRefreshInterval;
  useAdaptiveFetch(fetchServices, interval * 10);

  const updateEssenceList = (updates: EssenceViewModel[]) => {
    setEssences((existing) => {
      return updates.map((essence) => {
        const item = existing.find((i) => i.id == essence.id); // TODO we need some uuid in a private field for deletable objects
        if (item) {
          essence.selected = item.selected;
          essence.hidden = item.hidden;
        }
        return essence;
      });
    });
  };
  useWatchObject(essenceModels, updateEssenceList);

  useAdaptiveFetch(
    () =>
      fetchEssences(t).then((res) => {
        if (res.ok) {
          setInitialized(true);
          if (connectionLost) {
            triggerEvent('connection-restored');
            connectionLost = false;
          }
        } else if (res.problem === 'NETWORK_ERROR') {
          triggerEvent('connection-lost');
          connectionLost = true;
        }
      }),
    5000,
  );

  const fetchData = async () => {
    await fetchVideoInputs();
    fetchEssenceResolutions();
  };

  useEffect(() => {
    fetchData();
  }, []);

  watchEvent('preset-load', () => {
    fetchEssences(t);
    fetchData();
  });

  const filteredStart = () => essences.filter((e) => e.selected && essenceIsStopped(e.state));
  const handleStartEssense = () => {
    const filtered = filteredStart();
    if (filtered.length > 0) {
      Promise.all(filtered.map((e) => startStopEssence(e, true, true, t))).then((responses) => {
        triggerEvent('preset-update');
        const failed = responses.filter((r) => !r.ok);
        if (failed.length > 1) {
          // Message also in start/stop
          createAndDispatchNotification(
            t('essense.notifications.startErrorMsg', { count: failed.length }),
            NotificationVariant.ERROR,
            t,
          );
        }
      });
    }
  };

  const filteredStop = () => essences.filter((e) => e.selected && essenceIsStarted(e.state));
  const handleStopEssense = () => {
    const filtered = filteredStop();
    if (filtered.length > 0) {
      Promise.all(filtered.map((e) => startStopEssence(e, false, true, t))).then((responses) => {
        triggerEvent('preset-update');
        const failed = responses.filter((r) => !r.ok);
        if (failed.length > 1) {
          // Message also in start/stop
          createAndDispatchNotification(
            t('essense.notifications.stopErrorMsg', { count: failed.length }),
            NotificationVariant.ERROR,
            t,
          );
        }
      });
    }
  };

  const [deleteShown, showDelete, hideDelete] = useModalControls();
  const filteredDelete = () => essences.filter((e) => e.selected);
  const handleDeleteEssense = () => {
    const filtered = filteredDelete();
    if (filtered.length > 0) {
      Promise.all(filtered.map((e) => deleteEssence(e))).then((responses) => {
        triggerEvent('preset-update');
        const failed = responses.filter((r) => !r.ok);
        if (failed.length > 0) {
          createAndDispatchNotification(
            t('essense.notifications.deleteErrorMsg', { count: failed.length }),
            NotificationVariant.ERROR,
            t,
          );
        }
      });
    }
  };

  const statusTypes: Status[] = [
    { status: 'ACTIVE', color: colorValue('haiui-green-01') },
    { status: 'ALERT', color: colorValue('haiui-amber-01') },
    { status: 'INACTIVE', color: colorValue('haiui-gray-07') },
  ];

  const handleSelect = (item: EssenceViewModel, selected: boolean) => {
    const Essenses = essences.map((i: EssenceViewModel) => (i.id === item.id ? { ...i, selected } : i));
    setEssences(Essenses);
  };

  const onSelectAll = (selected: boolean) => {
    const models = essences.map((i) => ({ ...i, selected }));
    setEssences(models);
  };

  const handleOnKeyUp = (e: React.KeyboardEvent<HTMLElement>): void => {
    if (e.key === 'Enter') {
      const button = document.getElementById('essence-add-form-submit');
      if (!isNilOrEmpty(button)) {
        e.preventDefault();
        button.click();
      }
    }
  };

  const handleAddEssence = () => {
    setAddButtonState('pending');
    addEssenceRef.current.submitAddEssence().then((res: any) => {
      setAddButtonState(undefined);
      if (res?.ok) {
        const response = res.data;
        hideAddModal();
        scrollPanelIntoView({ id: response.info.id }, true);
      }
    });
  };

  const [searchFilters, setSearchFilters] = useLocalStorage(
    `${constant.lStorage.searchFilter}:${sessionStore.username}:essence`,
    [],
  );

  const sortInfo = {
    id: { title: t('sort.sortText.id'), type: SortType.GENERIC },
    name: { title: t('sort.sortText.name'), type: SortType.NATURAL_SORT },
    addressTxt: { title: t('sort.sortText.address'), type: SortType.NATURAL_SORT },
    typeTxt: { title: t('sort.sortText.type'), type: SortType.NATURAL_SORT },
    stateSortable: { title: t('sort.sortText.status'), type: SortType.GENERIC },
    inputChannel: { title: t('sort.sortText.input'), type: SortType.GENERIC },
  };
  return (
    <EssencesCtx.Provider value={{ essences, setEssences }}>
      <NmosServiceActiveContext.Provider value={nmosServiceActive}>
        {initialized && (isNil(essences) || essences.length === 0) ? (
          <NoContentAvailable
            title={t('essence.noContent.title')}
            subText={t('essence.noContent.subText')}
            btnText={t('essence.noContent.btnText')}
            iconName="Streams"
            onClick={showAddModal}
            btnDisabled={sessionStore.isUser() || nmosServiceActive}
          />
        ) : (
          <StreamingActionBar
            items={essences}
            showPlaceholder={false}
            searchKeys={['name', 'addressTxt', 'typeTxt', 'rtpPayload', 'sourceAddress']}
            initialSearchFilters={searchFilters}
            onChangeSearchFilters={setSearchFilters}
            sortInfo={sortInfo}
            statusTypes={statusTypes}
            getStateValue={essenceModelToStatus}
            actionButtons={
              sessionStore.isUser()
                ? []
                : [
                    {
                      onClick: handleStartEssense,
                      counter: filteredStart,
                      label: t('general.start'),
                    },
                    { onClick: handleStopEssense, counter: filteredStop, label: t('general.stop') },
                    { onClick: showDelete, counter: filteredDelete, label: t('general.deleteBtn') },
                  ]
            }
            List={EssenceList}
            onSelectAll={onSelectAll}
            updateSelection={handleSelect}
            storageKey="essence"
            actionRightPrimary={
              sessionStore.isUser()
                ? null
                : {
                    onClick: showAddModal,
                    disabled: essences?.findIndex((s) => s.id === -1) >= 0 || nmosServiceActive,
                    children: t('essence.settings.addEssence'),
                  }
            }
            nmosServiceActive={nmosServiceActive}
            placeholderRightComponents={1}
            placeholderNumPanels={4}
          />
        )}
        {deleteShown && (
          <Dialog
            headline={t('essence.settings.modal.delete.title', { count: filteredDelete().length })}
            bodyText={t('general.cannotUndo')}
            onClose={hideDelete}
            show={true}
            buttons={[
              { label: t('general.cancel') },
              { label: t('general.deleteBtn'), variant: 'danger', onClick: handleDeleteEssense },
            ]}
          />
        )}
        {addModalShown && (
          <Dialog
            title={t('essence.settings.addEssence')}
            dialogType="activity"
            headerIcon="Input"
            accentColor={constant.productColor}
            size="lg"
            className="mk-three-columns"
            content={<AddEssence model={newModel} ref={addEssenceRef} nmosServiceActive={nmosServiceActive} />}
            onClose={hideAddModal}
            onKeyUp={handleOnKeyUp}
            buttons={[
              { variant: 'secondary', onClick: hideAddModal, label: t('general.cancel'), disabled: nmosServiceActive },
              {
                id: 'essence-add-form-submit',
                variant: 'primary',
                state: addButtonState,
                close: false,
                onClick: handleAddEssence,
                label: t('essence.settings.addEssence'),
                disabled: nmosServiceActive,
              },
            ]}
          />
        )}
      </NmosServiceActiveContext.Provider>
    </EssencesCtx.Provider>
  );
};
